import { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, Divider, Space, Tag, Typography, Switch } from 'antd';
import { PageProps } from '@/types/PageProps';
import {
  useActiveInactiveUser,
  useUsersReduced,
} from '@/core/domain/enterprise/Users/Users.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { EditIcon, InfiniteTable } from '@/components/_common';
import { ColumnsType } from 'antd/lib/table';
import { UserReduced } from '@/core/domain/enterprise/Users/Users.types';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import UserModalForm from '@/components/Users/UserModalForm';
import { useDisclosure } from '@/hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import AccessDenied from '@/components/_common/AccessDenied';
import ClockIcon from '@/components/_common/ClockIcon';
import UserModalAccessClock from '@/components/Users/UserModalAccessClock';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';

const { Title } = Typography;

const UsersPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const [userSelected, setUserSelected] = useState<UserReduced>();
  const [modalOpened, modalActions] = useDisclosure(false, {
    onClose: () => setUserSelected(undefined),
  });
  const [modalClock, modalClockAction] = useDisclosure(false, {
    onClose: () => setUserSelected(undefined),
  });

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useUsersReduced();
  const users = flatInfiniteData(data);

  const activeInactiveUser = useActiveInactiveUser();

  const { data: matrixData } = useMatrix();

  const matrixActive = matrixData?.active;

  const columns: ColumnsType<UserReduced> = [
    {
      key: 'login',
      dataIndex: 'login',
      title: 'Login',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      key: 'email',
      dataIndex: 'email',
      title: 'E-mail',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      key: 'employeeName',
      dataIndex: 'employeeName',
      title: 'Funcionário',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      key: 'profiles',
      dataIndex: 'profiles',
      title: 'Perfis',
      width: COLUMNS_WIDTH.default,
      render: (_, item) => <Tag>{item.profileName}</Tag>,
    },
    {
      key: 'active',
      dataIndex: 'active',
      title: 'Situação',
      width: COLUMNS_WIDTH.default,
      render: (_, item) => {
        return (
          <>
            <Switch
              unCheckedChildren={intl.formatMessage({
                id: 'commons.entity.inactive',
              })}
              checkedChildren={intl.formatMessage({
                id: 'commons.entity.active',
              })}
              loading={activeInactiveUser.isLoading}
              checked={item.active}
              onClick={async () =>
                await activeInactiveUser.mutateAsync(item.id)
              }
              disabled={
                !hasPermissions([Authorities.CONFIGURATION_USER_EDIT_USERS]) ||
                !matrixActive
              }
            />
          </>
        );
      },
    },
    {
      key: 'action',
      title: 'Ação',
      width: 80,
      align: 'right',
      fixed: 'right',
      render: (_, row) => (
        <Space>
          <ClockIcon
            disabled={
              !hasPermissions([Authorities.CONFIGURATION_USER_EDIT_USERS]) ||
              !matrixActive
            }
            popover="Horários de Acesso"
            onClick={() => {
              modalClockAction.open();
              setUserSelected(row);
            }}
          />

          <Divider type="vertical" />

          <EditIcon
            disabled={
              !hasPermissions([Authorities.CONFIGURATION_USER_EDIT_USERS]) ||
              !matrixActive
            }
            iconProps={{
              onClick: () => {
                modalActions.open();
                setUserSelected(row);
              },
            }}
          />
        </Space>
      ),
    },
  ];

  if (
    !hasPermissions([
      Authorities.CONFIGURATION_USER_VIEW_USERS,
      Authorities.CONFIGURATION_MATRIX_SETTINGS,
    ])
  ) {
    return <AccessDenied />;
  }

  return (
    <div className="page">
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <div className="page-header" style={{ marginBottom: 24 }}>
        <Title level={4}>
          <FormattedMessage id={route.name} />
        </Title>

        <Button
          disabled={!hasPermissions([Authorities.CONFIGURATION_USER_ADD_USERS])}
          type="primary"
          onClick={modalActions.open}
        >
          <FormattedMessage id="users.create" />
        </Button>
      </div>

      <Divider />

      <InfiniteTable
        dataSource={users}
        columns={columns}
        loading={isLoading}
        isFetching={isFetching}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        scroll={{ x: getTableScrollX(columns) }}
      />

      <UserModalForm
        open={modalOpened}
        onCancel={modalActions.close}
        user={userSelected}
      />

      <UserModalAccessClock
        open={modalClock}
        onCancel={modalClockAction.close}
        user={userSelected}
      />
    </div>
  );
};

UsersPage.propTypes = {
  route: PropTypes.object.isRequired,
};

export default UsersPage;
